import { inject, Injector, runInInjectionContext } from '@angular/core';

import { WaitingOnYouSignalState, WaitingOnYouV2SignalState } from '../store';
import {
  FEATURE_FLAGS_SIGNAL_STORE,
  FeatureFlag,
} from '@data-access/system-controls';
import {
  DASHBOARD_LAYOUT_KEYS_ENUM,
  DashboardWaitingOnYouRequestParamsV2,
  DEFAULT_WAITING_ON_REQUEST_PARAMS,
} from '@data-access/dash-board';
import { filter, map, Observable, of, switchMap, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * Request params for the first load of the redesigned Waiting on you card:
 * the unfiltered `all` tab, first 10 rows.
 */
const WAITING_ON_YOU_V2_DEFAULT_REQUEST_PARAMS: DashboardWaitingOnYouRequestParamsV2 =
  {
    ...DEFAULT_WAITING_ON_REQUEST_PARAMS,
  };

function _resolveWaitingOnYouV1Data(
  state: WaitingOnYouSignalState,
  injector: Injector,
) {
  return runInInjectionContext(injector, (): Observable<boolean> => {
    const _waitingOnYouData = state.$data();
    if (_waitingOnYouData) {
      return of(true);
    }
    state.getWaitingOnYouStates({
      requestParams: {
        filter: 'all',
        limit: 10,
        offSet: 0,
      },
    });
    return toObservable(state.$data).pipe(
      filter((data) => data !== null),
      take(1),
      map(() => true),
    );
  });
}

function _resolveWaitingOnYouV2Data(
  state: WaitingOnYouV2SignalState,
  injector: Injector,
) {
  return runInInjectionContext(injector, (): Observable<boolean> => {
    const _data = state.$waitingOnYouData();
    if (_data) {
      return of(true);
    }
    state.fetchWaitingOnYou({
      payload: WAITING_ON_YOU_V2_DEFAULT_REQUEST_PARAMS,
    });

    return toObservable(state.$waitingOnYouData).pipe(
      filter((data) => data !== null),
      take(1),
      map(() => true),
    );
  });
}

export const waitingOnYouResolver = () => {
  const _injector = inject(Injector);
  const _onYouSignalState = inject(WaitingOnYouSignalState);
  const _onYouV2SignalState = inject(WaitingOnYouV2SignalState);
  const _featureFlags = inject(FEATURE_FLAGS_SIGNAL_STORE);

  return toObservable(_featureFlags.$isReady).pipe(
    filter((isReady) => isReady),
    take(1),
    map(() =>
      _featureFlags.$selectFeature(
        FeatureFlag.DASHBOARD_IMPROVEMENTS,
        DASHBOARD_LAYOUT_KEYS_ENUM.WAITING_ON_YOU,
      )(),
    ),
    switchMap((isEnabled) =>
      isEnabled
        ? _resolveWaitingOnYouV2Data(_onYouV2SignalState, _injector)
        : _resolveWaitingOnYouV1Data(_onYouSignalState, _injector),
    ),
  );
};
